import styled from "@emotion/styled";
import { DatePicker, Typography } from "@kaltura/ds-react-components";
import FormControl from "@mui/material/FormControl";
import { KmsTypeFilterItemDate } from "@kaltura/mediaspace-shared-types";
import { useCallback, useEffect, useId, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { FilterItemAdditionalProps } from "./FilterItemAdditionalProps";

export type DateFilterItemProps = KmsTypeFilterItemDate & FilterItemAdditionalProps;

const StyledDateFilterItem = styled(FormControl)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    maxWidth: 150,
}));

const StyledFieldLabel = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.tone2,
    marginBottom: theme.spacing(1),
}));

/**
 * date filter item - a single date-field in a filter
 */
export function DateFilterItem({
    param,
    label,
    value,
    defaultValue,
    disabled,
    onChange,
    dateFormat,
}: DateFilterItemProps) {
    const id = useId();
    const inputId = `input-${id}`;
    const labelId = `label-${id}`;

    const sendButtonAnalytics = useButtonAnalytics();

    const convertValueToDayjs = useCallback(
        (value: string) => {
            // outside the component we use unix timestamp (ms) as a string.
            // DatePicker component takes a dayjs object.
            return dayjs(parseInt(value));
        },
        []
    );

    // current date: if value is given use that. otherwise, if default value is given, use that.
    const [currentDate, setCurrentDate] = useState<Dayjs | null>(() =>
        value ? convertValueToDayjs(value) : defaultValue ? convertValueToDayjs(defaultValue) : null
    );

    const handleChange = (date: Dayjs | null) => {
        const eventName = `Filters - ${param}${date ? "" : " - Clear"}`;
        sendButtonAnalytics(eventName, ButtonClickAnalyticsType.FILTER);
        if (date) {
            onChange(param, date.startOf("day").valueOf().toString(), true, false);
            return;
        }
        // clear:
        onChange(param, "", false, false);
    };

    useEffect(() => {
        if (!value) {
            return;
        }
        setCurrentDate(convertValueToDayjs(value));
    }, [value, convertValueToDayjs]);

    return (
        <StyledDateFilterItem>
            <StyledFieldLabel component={"label"} variant={"formLabel"} id={labelId} htmlFor={inputId}>
                {label}
            </StyledFieldLabel>
            <DatePicker
                id={inputId}
                ariaLabelledby={labelId}
                onChange={handleChange}
                value={currentDate}
                disabled={disabled}
                format={dateFormat}
            />
        </StyledDateFilterItem>
    );
}

export default DateFilterItem;
